import React, { useEffect, useRef, useState } from "react";

interface VisibleElement {
  [key: string]: boolean;
}

const Founder: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const founderRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState<VisibleElement>({});

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    const mainObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible((prev) => ({ ...prev, main: true }));
        }
      },
      { threshold: 0.3 }
    );

    const founderObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible((prev) => ({ ...prev, founder: true }));
        }
      },
      { threshold: 0.4 }
    );

    if (sectionRef.current) {
      mainObserver.observe(sectionRef.current);
      observers.push(mainObserver);
    }

    if (founderRef.current) {
      founderObserver.observe(founderRef.current);
      observers.push(founderObserver);
    }

    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  return (
    <div className="min-h-screen bg-gray-900">
      <section
        ref={sectionRef}
        className="relative w-full py-16 bg-gray-900 border-t border-gray-700"
      >
        <div className="max-w-7xl mx-auto px-7">
          <div
            className={`mb-8 transition-all duration-1000 ${
              visible.main
                ? "animate-fade-in-up opacity-100"
                : "opacity-0 translate-y-8"
            }`}
          >
            <div className="inline-block px-4 py-2 bg-gray-800 border border-gray-600 rounded-full text-sm text-gray-300">
              WHO WE ARE
            </div>
          </div>

          <h2
            className={`text-white text-6xl md:text-7xl font-medium leading-tight mb-8 transition-all duration-1000 delay-300 ${
              visible.main
                ? "animate-slide-in-left opacity-100"
                : "opacity-0 -translate-x-12"
            }`}
          >
            Meet the Founders
          </h2>

          <p
            id="founder-section"
            className={`text-gray-400 text-lg font-light max-w-3xl transition-all duration-1000 delay-500 ${
              visible.main
                ? "animate-slide-in-right opacity-100"
                : "opacity-0 translate-x-12"
            }`}
          >
            At DEVANANTA R&D, our greatest strength lies in the diverse and
            talented individuals who make up our team. From brilliant
            researchers to visionary entrepreneurs, we're united by a shared
            passion for innovation and a common goal.
          </p>
        </div>
      </section>

      <section
        ref={founderRef}
        className="w-full py-20 bg-gradient-to-br from-gray-900 to-black border-t border-gray-800"
      >
        <div className="max-w-7xl mx-auto px-7">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
            <div className="flex justify-center lg:justify-center">
              <div
                className={`relative w-[420px] h-[525px] rounded-2xl overflow-hidden transition-all duration-1000 delay-700 ${
                  visible.founder
                    ? "animate-zoom-in opacity-100 scale-100"
                    : "opacity-0 scale-95"
                }`}
              >
                <img
                  src="/devyansh.png"
                  alt="Advanced Battery Technology"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="space-y-8">
              <div
                className={`transition-all duration-800 delay-300 ${
                  visible.founder
                    ? "animate-fade-in-up opacity-100"
                    : "opacity-0 translate-y-8"
                }`}
              >
                <h3 className="text-5xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent mb-4">
                  DEVYANSH AGRAWAL
                </h3>

                <div className="flex items-center gap-3 mt-2 mb-4">
                  <div className="inline-block px-5 py-2 bg-gray-800 border border-gray-600 rounded-full backdrop-blur-sm">
                    <span className="text-sm text-blue-400 font-semibold tracking-wide">
                      Founder
                    </span>
                  </div>

                  <a
                    href="https://devyanshagrawal.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-2 px-4 py-2 bg-gray-800/70 border border-gray-700 rounded-full backdrop-blur-sm hover:bg-blue-900/30 hover:border-blue-500 transition-all duration-300 hover:scale-105"
                    aria-label="View Devyansh Agrawal's Portfolio"
                  >
                    <svg
                      className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                    <span className="text-sm text-gray-300 group-hover:text-blue-300 transition-colors duration-300">
                      View Portfolio
                    </span>
                  </a>
                </div>
              </div>

              <div
                className={`transition-all duration-800 delay-500 ${
                  visible.founder
                    ? "animate-fade-in-up opacity-100"
                    : "opacity-0 translate-y-8"
                }`}
              >
                <p className="text-gray-300 text-lg font-light leading-relaxed">
                  Devyansh Agrawal — Founder of Devananta R&D Pvt. Ltd., is
                  passionate about pioneering innovation in advanced materials,
                  energy, defence and sustainable technologies. With a vision to
                  drive transformative research and scalable solutions, he leads
                  Devananta's mission to build a future-ready ecosystem at the
                  intersection of science, industry, and society.
                </p>
                <p className="text-gray-300 text-lg font-light leading-relaxed mt-5">
                  Having a strong background in engineering and strategic
                  leadership, he combines a passion for cutting-edge research
                  with a commitment to solving real-world challenges,
                  positioning the company as a leader in the tech and
                  sustainability space.
                </p>
              </div>

              <div
                className={`transition-all duration-800 delay-700 ${
                  visible.founder
                    ? "animate-fade-in-up opacity-100"
                    : "opacity-0 translate-y-8"
                }`}
              >
                <div className="flex flex-wrap gap-4 mt-6">
                  <div className="px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg hover:border-blue-500 transition-all duration-300 hover:scale-105">
                    <span className="text-sm text-gray-300">
                      Advanced Materials
                    </span>
                  </div>
                  <div className="px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg hover:border-purple-500 transition-all duration-300 hover:scale-105">
                    <span className="text-sm text-gray-300">
                      Sustainable Tech
                    </span>
                  </div>
                  <div className="px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg hover:border-blue-500 transition-all duration-300 hover:scale-105">
                    <span className="text-sm text-gray-300">
                      Defense Innovation
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Founder;
